import { EventEmitter, Injectable, Output } from "@angular/core";

// Models
import { TranslateService } from "@ngx-translate/core";
import { ContextLocationViewModel } from "../models/main/context-location.model";
import { LinkItem } from "../models/main/link-item.model";
import { PlaceViewModel } from "../models/main/place.model";
import { Policies } from "../models/main/policies.enum";
import { ApplicationViewModel } from "../models/master/application.model";
import { BehaviorSubject } from "rxjs";

export interface MigaDePan {
	nombre: string;
	url: string;
}

@Injectable({
	providedIn: "root",
})
export class GlobalService {
	@Output() changesDetected = new EventEmitter<string>();
	private readonly migasSubject = new BehaviorSubject<MigaDePan[]>([]);
	public migas$ = this.migasSubject.asObservable();

	constructor(private readonly _translateService: TranslateService) {
		const migasGuardadas = localStorage.getItem("migasDePan");
		if (migasGuardadas) {
			this.migasSubject.next(JSON.parse(migasGuardadas));
		}

		// Sincronizar cada cambio con localStorage
		this.migas$.subscribe((migas) => {
			localStorage.setItem("migasDePan", JSON.stringify(migas));
		});
	}

	// Properties
	public breadcrumbs: LinkItem[];
	public current: LinkItem;
	public menu: LinkItem[];
	public locations: ContextLocationViewModel[];
	public lastPlace: ContextLocationViewModel;
	public toolbar: ApplicationViewModel[];

	// Attributes
	private readonly _places: PlaceViewModel[];
	private _startBreadcrumb: LinkItem[];
	private _locationBreadcrumb: LinkItem[];
	private _endBreadcrumb: LinkItem[];

	private _lastStart: LinkItem[];
	private _lastEnd: LinkItem[];
	private _lastMenu: LinkItem[];

	// Public methods
	setPlaces(places: PlaceViewModel[]) {
		this._setPlaces(places);
	}

	getLocations(): ContextLocationViewModel[] {
		return this.locations;
	}

	setLocations(locations: ContextLocationViewModel[]) {
		this.locations = locations;
	}

	// Obtener copia actual
	private get migas(): MigaDePan[] {
		return this.migasSubject.getValue();
	}

	actualizarMigas(migas: MigaDePan[]) {
		this.migasSubject.next(migas);
	}

	agregarMiga(miga: MigaDePan) {
		const nuevas = [...this.migas, miga];
		this.migasSubject.next(nuevas);
	}

	eliminarUltimaMiga() {
		const nuevas = this.migas.slice(0, -1);
		this.migasSubject.next(nuevas);
	}

	eliminarMigaPorNombre(nombre: string) {
		const nuevas = this.migas.filter((m) => m.nombre !== nombre);
		this.migasSubject.next(nuevas);
	}

	limpiarMigas() {
		this.migasSubject.next([]);
		localStorage.removeItem("migasDePan");
	}

	agregarMigaUnica(nuevaMiga: MigaDePan) {
		const migas = this.migas;
		const indiceExistente = migas.findIndex(
			(m) => m.nombre === nuevaMiga.nombre
		);

		if (indiceExistente !== -1) {
			// La miga ya existe → cortar desde esa posición hacia adelante
			const nuevas = migas.slice(0, indiceExistente + 1);
			this.migasSubject.next(nuevas);
		} else {
			// No existe → simplemente agregarla
			this.migasSubject.next([...migas, nuevaMiga]);
		}
	}

	agregarCebeToMiga(nuevaMiga: MigaDePan) {
		const migas = this.migas;
		const indice = migas.findIndex(
			(obj) => this.getSegmentBeforeId(obj.url) === "cebe"
		);
		if (indice !== -1) {
			migas.splice(indice);
		}
		this.migasSubject.next([...migas, nuevaMiga]);
	}

	agregarCentroToMiga(nuevaMiga: MigaDePan) {
		const migas = this.migas;
		const indice = migas.findIndex(
			(obj) => this.getSegmentBeforeId(obj.url) === "centro"
		);
		if (indice !== -1) {
			migas.splice(indice);
		}
		this.migasSubject.next([...migas, nuevaMiga]);
	}

	getSegmentBeforeId(url: string): string | null {
		const match = url.match(/#\/([^\/]+)\/\d+$/);
		return match ? match[1] : null;
	}

	setHeaderOptions(
		start: LinkItem[],
		place: ContextLocationViewModel,
		end: LinkItem[],
		menu: LinkItem[],
		toolbar: ApplicationViewModel[] = []
	) {
		this._lastStart = start;
		this.lastPlace = place;
		this._lastEnd = end;
		this._lastMenu = menu;

		this._startBreadcrumb = start;
		this._setContextLocation(place);
		this._endBreadcrumb = end;

		this.breadcrumbs = [];
		this.breadcrumbs.push(...this._startBreadcrumb);
		this.breadcrumbs.push(...this._locationBreadcrumb);
		this.breadcrumbs.push(...this._endBreadcrumb);

		if (this._locationBreadcrumb && this._locationBreadcrumb.length > 0) {
			this.current =
				this._locationBreadcrumb[this._locationBreadcrumb.length - 1];
		} else {
			this.current = {
				text: "",
				url: "",
			};
		}

		this.menu = menu;
		this.toolbar = toolbar;

		this.changesDetected.emit("Cambio en parámetros globales");
	}

	// Private methods
	private _setPlaces(places: ContextLocationViewModel[]) {
		this.locations = [];
		this.locations = places;
		localStorage.removeItem("location");
		localStorage.setItem("location", btoa(unescape(encodeURIComponent(JSON.stringify(this.locations)))));
		if (
			this._lastStart?.length > 0 ||
			this.lastPlace ||
			this._lastEnd?.length > 0 ||
			this._lastMenu?.length > 0
		) {
			this.setHeaderOptions(
				this._lastStart,
				this.lastPlace,
				this._lastEnd,
				this._lastMenu,
				this.toolbar
			);
		}
	}

	private _setContextLocation(place: ContextLocationViewModel) {
		let currentLocation: ContextLocationViewModel;
		this._locationBreadcrumb = [];
		/* if (this.locations && place) {
            this._locationBreadcrumb = [];
            if (place.site) {
                currentLocation = (this.locations.find(x => x.site && x.site.toUpperCase() == place.site.toUpperCase()));
            } else if (place.service) {
                currentLocation = (this.locations.find(x => x.service && x.service.toUpperCase() == place.service.toUpperCase()
                    && (x.site ?? "") == ""));
            } else if (place.department) {
                currentLocation = (this.locations.find(x => x.department && x.department.toUpperCase() == place.department.toUpperCase()
                    && (x.site ?? "") == "" && (x.service ?? "") == ""));
            } else if (place.area) {
                currentLocation = (this.locations.find(x => x.area && x.area.toUpperCase() == place.area.toUpperCase()
                    && (x.site ?? "") == "" && (x.service ?? "") == "" && (x.department ?? "") == ""));
            } else if (place.country) {
                currentLocation = (this.locations.find(x => x.country && x.country.toUpperCase() == place.country.toUpperCase()
                    && (x.site ?? "") == "" && (x.service ?? "") == "" && (x.department ?? "") == ""));
                // Sólo pongo el país en la breadcrumb si es un país lo que tenemos que mostrar.
                if (currentLocation.country) {
                    this._locationBreadcrumb.push({
                        text: this._translateService.instant('countries.' + currentLocation.country),
                        url: '/country/' + currentLocation.country,
                        active: currentLocation.hasAccess,
                        policies: [Policies.Admin] // TODO: Cambiar por la política correcta
                    });
                }
            } */
		if (currentLocation && currentLocation.area) {
			this._locationBreadcrumb.push({
				text: currentLocation.areaName,
				url: `/area/${  currentLocation.area}`,
				active:
					this.locations.find(
						(x) =>
							x.typeName == "Area" &&
							x.area == currentLocation.area
					)?.hasAccess || false,
				policies: [Policies.Admin], // TODO: Cambiar por la política correcta
			});
		}
		if (currentLocation && currentLocation.department) {
			this._locationBreadcrumb.push({
				text: currentLocation.departmentName,
				url: `/department/${  currentLocation.department}`,
				active:
					this.locations.find(
						(x) =>
							x.typeName == "Department" &&
							x.department == currentLocation.department
					)?.hasAccess || false,
				policies: [Policies.Admin], // TODO: Cambiar por la política correcta
			});
		}
		/*             if (currentLocation && currentLocation.service) {
                        this._locationBreadcrumb.push({
                            text: currentLocation.serviceName,
                            url: '/service/' + currentLocation.service,
                            active: this.locations.find(x => x.typeName == "Service" && x.service == currentLocation.service)?.hasAccess || false,
                            policies: [Policies.Admin] // TODO: Cambiar por la política correcta
                        })
                    } */
		if (place && place.site) {
			this._locationBreadcrumb.push({
				text:
					`(${ 
					currentLocation.site 
					}) ${ 
					currentLocation.siteName}`,
				url: `/centro/${  currentLocation.site}`,
				active:
					this.locations.find(
						(x) =>
							x.typeName == "Site" &&
							x.site == currentLocation.site
					)?.hasAccess || false,
				policies: [Policies.Admin], // TODO: Cambiar por la política correcta
			});
		}
	}
}
